import { TradeAndWrapWrapper } from "wrappers/TradeAndWrapWrapper/TradeAndWrapWrapper";
import { useIsMobile } from "hooks/useDeviceType";
import React, { useEffect, useMemo } from "react";
import { useEffectOnce } from "usehooks-ts";
import { SwitchNetwork } from "../../components/Modal/instance/SwitchNetwork/SwitchNetwork";
import { useTimedFetchTokens } from "../../hooks/useTimedFetchTokens";
import { useUpdateAddressWithENS } from "../../hooks/useUpdateAddressWithENS";
import { GlobalStore, resetOrderState, resetWrapState } from "../../store/global/GlobalStore";
import { getTokenMetadata } from "../../store/global/getTokenMetadata";
import { OrderStatus } from "../../types/orderStatus";
import { OrderCompleted } from "./OrderCompleted";
import { OrderEntry } from "./OrderEntry";
import { OrderFailed } from "./OrderFailed";
import { OrderInProgress } from "./OrderInProgress";
import { ReviewOrder } from "./ReviewOrder";

export interface TradeProps {}

export const Trade: React.FC<TradeProps> = () => {
  /**
   * Auto-fetch tokens while the app is running
   * This is for /Trade tokens
   */
  useTimedFetchTokens();
  useUpdateAddressWithENS();

  const networkName = GlobalStore.useState(s => s.network.name);
  getTokenMetadata.useBeckon(networkName);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  useEffectOnce(() => {
    const script = document.createElement("script");
    script.src = "https://bebop.statuspage.io/embed/script.js";
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  });

  const { orderStatus } = GlobalStore.useState(s => {
    return {
      orderStatus: s.orderStatus,
    };
  });
  return (
    <>
      <TradeComponents orderStatus={orderStatus} />
      <SwitchNetwork />
    </>
  );
};

const TradeComponents: React.FC<{ orderStatus: OrderStatus }> = React.memo(({ orderStatus }) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    return () => {
      //On unmount reset the store
      resetOrderState();

      // Wrap state reset needed temporarily as some of its logic is used in
      // /trade, e.g. in ReviewOrder.tsx: `{wrapQuote?.amount != null &&
      // <WrapActionButton />}`
      resetWrapState();
    };
  }, []);

  const content = useMemo(() => {
    if (orderStatus === OrderStatus.ENTRY || orderStatus === OrderStatus.QUOTING) {
      return <OrderEntry />;
    }
    if (orderStatus === OrderStatus.REVIEW || orderStatus === OrderStatus.CONFIRMING) {
      return <ReviewOrder />;
    }
    if (orderStatus === OrderStatus.IN_PROGRESS) {
      return <OrderInProgress />;
    }

    if (orderStatus === OrderStatus.COMPLETED) {
      return <OrderCompleted />;
    }
    if (orderStatus === OrderStatus.ERROR) {
      return <OrderFailed />;
    }
    throw new Error(`No order status mapping for:${orderStatus}`);
  }, [orderStatus]);

  return <TradeAndWrapWrapper isMobile={isMobile}>{content}</TradeAndWrapWrapper>;
});
